// ***************************************************************************************
// Every version of Word Soup that is playable, and where it lives.
//
// ONE LIST, read by two things: the hub page that offers them, and the footer link on
// each game that points back at the hub. A mode is added here, and both of those follow -
// which matters because the whole point of the hub is that nobody should have to remember
// what exists.
//
// This is deployment knowledge, not game rules, so it is deliberately NOT in the engine:
// the engine defines what Ladder IS, and this says which hostname happens to serve it.
// The two can be wrong independently - a site that serves a mode it does not list, or
// lists one it cannot serve - so `npm run sites:check` asks each host what it is and
// compares the answer with this list.
// ***************************************************************************************

export interface Site {
  /**
   * The mode id its Worker serves, matching SITE_MODE and modeById().
   *
   * NULL for a version that is not a mode of this engine at all - the Emscripten build of
   * the original C++ game is a different program that happens to be the same game, with
   * no API, no daily puzzle and no shared leaderboard. It belongs on the hub, because the
   * hub answers "what is there to play", and it cannot be checked like the others.
   */
  readonly modeId: string | null;
  readonly title: string;
  readonly host: string;
  /** What it is, in one line. */
  readonly what: string;
  /** What is DIFFERENT about it, which is the part worth knowing when choosing. */
  readonly twist: string;
}

/**
 * The game's own domain (7 Oct 2026). Every version lives under it, and nothing a player
 * sees names the account behind it: each Worker's own Cloudflare address is switched off in
 * its Wrangler config. This file ships in the public source map, so keep it that way.
 */
export const DOMAIN = 'wordsoupgame.com';

/**
 * The bare domain. Until one version is chosen for release it sends players on to the hub
 * (worker/hub.ts); at release it becomes that version's address, ahead of the marketing.
 */
export const HOME_HOST = `https://${DOMAIN}`;

export const HUB_HOST = `https://hub.${DOMAIN}`;

export const SITES: readonly Site[] = [
  {
    modeId: 'daily-score-attack',
    title: 'Daily Score Attack',
    host: `https://daily.${DOMAIN}`,
    what: 'Make your own words on a 5×5 until nothing is left to play.',
    twist: 'The original. No targets, no timer — the score is the whole game.',
  },
  {
    modeId: 'ladder',
    title: 'Ladder',
    host: `https://ladder.${DOMAIN}`,
    what: 'Find one word of each length: 3, 4, 5, 6 and 7. Any order.',
    twist: 'The five rungs use up the grid exactly, so a finished ladder empties it.',
  },
  {
    modeId: 'hunt',
    title: 'Word Soup Hunt',
    host: `https://hunt.${DOMAIN}`,
    what: 'Five challenges a day. Take one and that word scores double.',
    twist: 'Every challenge has an answer on the board — including the definition clues.',
  },
  {
    modeId: 'freedom',
    title: 'Word Soup Freedom',
    host: `https://freedom.${DOMAIN}`,
    what: 'Take letters from anywhere. They do not have to touch.',
    twist: 'The grid stops being a maze and becomes a bag of 25 letters.',
  },
  {
    modeId: null,
    title: 'Word Soup Full',
    host: `https://full.${DOMAIN}`,
    what: 'The original game, all of it — every mode, hints, progress, the lot.',
    twist: 'The C++ build compiled to WebAssembly: a 21MB download, with the daily games on a shared leaderboard.',
  },
];

export function siteFor(modeId: string): Site | null {
  return SITES.find((site) => site.modeId === modeId) ?? null;
}

/** The versions this engine serves, which are the ones with an API to check. */
export function engineSites(): Site[] {
  return SITES.filter((site) => site.modeId !== null);
}
